// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// game.ts — a played game: the position, the ply log that produced it, and the resolution.
//
// THE LOG IS THE GAME. brood has no randomness and no hidden information, so the entire state is a
// pure function of (mode, plies). That is what makes the multiplayer shape possible: a peer can
// send its whole log in every message, a dropped datagram is unobservable, a late joiner catches
// up from one message, and a peer running a different build is DETECTED — its log fails to replay
// through our own generator — rather than silently diverging.
//
// The repetition ledger lives here rather than in `rules.ts` because it is a property of the game
// as played, not of the position: the same position reached by a different route is only a draw
// once you have been there three times in THIS game.

import { modeOf, type Mode, type ModeId } from './modes';
import {
  type Move,
  type Outcome,
  type Reason,
  Position,
  apply,
  closedSides,
  isLegal,
  legalMoves,
  moveOf,
  outcome,
  plyOf,
  positionKey,
} from './rules';

export const RETIRE_PLY = 2; // a pass; used as the walkover marker when a peer vanishes

export interface Standing {
  readonly seat: number;
  /** Sides of this seat's Matriarch that were closed at the end. */
  readonly closed: number;
  readonly placed: number;
  readonly moved: number;
  /** How close this seat got to closing the OTHER Matriarch, at its best moment in the game. */
  readonly bestPressure: number;
}

export class Game {
  readonly mode: Mode;
  readonly pos: Position;
  readonly plies: number[] = [];
  /** How many times each position has been seen. Threefold is a draw. */
  private readonly seen = new Map<string, number>();
  private resolution: Outcome = { over: false };
  /** Peak closed-sides each seat inflicted on the other, for the results breakdown. */
  private readonly pressure: [number, number] = [0, 0];
  private readonly acts: [number, number] = [0, 0];
  private readonly moves: [number, number] = [0, 0];

  constructor(mode: Mode | ModeId | string) {
    this.mode = typeof mode === 'string' ? modeOf(mode) : mode;
    this.pos = new Position(this.mode);
    this.seen.set(positionKey(this.pos), 1);
  }

  get over(): boolean {
    return this.resolution.over;
  }
  get winner(): number {
    return this.resolution.over ? this.resolution.winner : -1;
  }
  get reason(): Reason | null {
    return this.resolution.over ? this.resolution.reason : null;
  }
  get turn(): 0 | 1 {
    return this.pos.turn;
  }
  get ply(): number {
    return this.pos.ply;
  }

  legal(): Move[] {
    return this.over ? [] : legalMoves(this.pos);
  }

  /** Play a move. Returns false if it is not legal here, which is also how a replay detects that
   *  the sender is running different code. */
  play(m: Move): boolean {
    if (this.over) return false;
    if (!isLegal(this.pos, m)) return false;
    const seat = this.pos.turn;
    apply(this.pos, m);
    this.plies.push(plyOf(m));
    this.acts[seat]++;
    if (m.t === 'move' || m.t === 'shunt') this.moves[seat]++;

    const foe = seat ^ 1;
    const closed = closedSides(this.pos, foe);
    if (closed > this.pressure[seat]) this.pressure[seat] = closed;

    const k = positionKey(this.pos);
    const n = (this.seen.get(k) ?? 0) + 1;
    this.seen.set(k, n);

    const o = outcome(this.pos);
    if (o.over) this.resolution = o;
    else if (n >= 3) this.resolution = { over: true, winner: -1, reason: 'repetition' };
    return true;
  }

  /** Play a packed ply. */
  playPly(ply: number): boolean {
    const m = moveOf(ply);
    return m !== null && this.play(m);
  }

  /** End the game because a peer left. Recorded as a resolution, never as a ply — the log stays a
   *  pure record of moves, and a walkover is a fact about the room rather than about the board. */
  walkover(winner: 0 | 1): void {
    if (this.over) return;
    this.resolution = { over: true, winner, reason: 'walkover' };
  }

  /** Rebuild a game from a log. Returns null if any ply is illegal — which is the whole point:
   *  a peer on a different build fails loudly here instead of diverging in silence. */
  static replay(mode: Mode | ModeId | string, plies: readonly number[]): Game | null {
    const g = new Game(mode);
    for (const ply of plies) {
      if (g.over) return null; // moves after the game ended are not a game we recognise
      if (!g.playPly(ply)) return null;
    }
    return g;
  }

  standings(): Standing[] {
    return [0, 1].map((seat) => ({
      seat,
      closed: closedSides(this.pos, seat),
      placed: this.acts[seat] - this.moves[seat],
      moved: this.moves[seat],
      bestPressure: this.pressure[seat],
    }));
  }
}
